import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { submitFeedback } from '@/services/feedbackService';
import { analytics } from '@/lib/analytics';
import { useDocumentationTranslations } from '@/hooks/useDocumentationTranslations';
import { logError } from '@/utils/errorHandler';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const docs = useDocumentationTranslations();
  const { t } = useTranslation();

  // Create validation schema with translated messages
  const feedbackSchema = z.object({
    message: z
      .string()
      .min(10, docs.feedback.validation.messageMinLength)
      .max(5000, docs.feedback.validation.messageMaxLength),
    email: z.string().email(docs.feedback.validation.emailInvalid).optional().or(z.literal('')),
  });

  type FeedbackFormData = z.infer<typeof feedbackSchema>;

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      message: '',
      email: '',
    },
  });

  const watchedMessage = form.watch('message');
  const characterCount = watchedMessage?.length || 0;
  const maxCharacters = 5000;
  const isNearLimit = characterCount > maxCharacters * 0.8;

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true);

    try {
      await submitFeedback({
        message: data.message,
        email: data.email || undefined,
        page_url: window.location.href,
      });

      // Track analytics
      analytics.trackFeedbackSubmitted(!!data.email);

      // Show success message
      toast.success(docs.feedback.toast.successTitle, {
        description: docs.feedback.toast.successDescription,
      });

      // Reset form and close modal
      form.reset();
      onClose();
    } catch (error: any) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'FeedbackModal',
        action: 'onSubmit',
        metadata: {
          hasEmail: !!data.email,
          messageLength: data.message.length,
          context: 'feedback_submission_failure',
        },
      });

      // Handle specific error cases with proper translations
      let errorMessage = docs.feedback.toast.errorDefault;

      if (error.response?.status === 429) {
        errorMessage = t('errors.feedbackModal.rateLimitExceeded');
      } else if (error.response?.status === 401) {
        errorMessage = t('errors.feedbackModal.authenticationRequired');
      } else if (error.response?.status === 400) {
        // Handle validation errors
        const responseData = error.response.data;
        if (responseData?.message?.includes('10 characters')) {
          errorMessage = t('errors.feedbackModal.messageTooShort');
        } else if (responseData?.message?.includes('5000 characters')) {
          errorMessage = t('errors.feedbackModal.messageTooLong');
        } else if (responseData?.message?.includes('email')) {
          errorMessage = t('errors.feedbackModal.invalidEmail');
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else {
          errorMessage = t('errors.feedbackModal.invalidMessage');
        }
      } else if (error.response?.status === 500) {
        errorMessage = t('errors.feedbackModal.serverError');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === 'NETWORK_ERROR' || error.name === 'NetworkError') {
        errorMessage = t('errors.feedbackModal.networkError');
      }

      toast.error(docs.feedback.toast.errorTitle, {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="size-5" />
            {docs.feedback.title}
          </DialogTitle>
          <DialogDescription>{docs.feedback.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Message Field */}
          <div className="space-y-2">
            <Label htmlFor="message">
              {docs.feedback.form.messageLabel}{' '}
              <span className="text-red-500">{docs.feedback.form.messageRequired}</span>
            </Label>
            <Textarea
              id="message"
              placeholder={docs.feedback.form.messagePlaceholder}
              {...form.register('message')}
              className={`min-h-[120px] resize-none ${
                form.formState.errors.message ? 'border-red-500' : ''
              }`}
              disabled={isSubmitting}
            />
            <div className="flex items-center justify-between text-xs">
              <div>
                {form.formState.errors.message && (
                  <span className="text-red-500">{form.formState.errors.message.message}</span>
                )}
              </div>
              <div className={`${isNearLimit ? 'text-orange-500' : 'text-muted-foreground'}`}>
                {docs.getCharacterCount(characterCount, maxCharacters)}
              </div>
            </div>
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email">{docs.feedback.form.emailLabel}</Label>
            <Input
              id="email"
              type="email"
              placeholder={docs.feedback.form.emailPlaceholder}
              {...form.register('email')}
              className={form.formState.errors.email ? 'border-red-500' : ''}
              disabled={isSubmitting}
            />
            {form.formState.errors.email && (
              <span className="text-xs text-red-500">{form.formState.errors.email.message}</span>
            )}
            <p className="text-xs text-muted-foreground">{docs.feedback.form.emailHelp}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {docs.feedback.form.buttons.cancel}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !form.watch('message')?.trim()}
              className="min-w-[100px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {docs.feedback.form.buttons.sending}
                </>
              ) : (
                <>
                  <Send className="mr-2 size-4" />
                  {docs.feedback.form.buttons.send}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
